import Button from 'components/Button';
import Icon from 'components/Icon';
import LinkButton from 'components/LinkButton';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import TruncateRight from 'components/Truncator/TruncateRight';
import ExportHistoryDataModal, {
    useExportHistoryDataModal,
} from 'pages/History/components/ExportModal';
import {FC} from 'react';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import updateUrlParamsAndReload from 'utils/pagination';
import {setUrl} from 'utils/request';

import {useDeleteLineItem} from '../services';
import {CatalogLineItem, CatalogLineItemBackend} from '../types';
import DeleteAllCatalogItems, {
    useDeleteAllLineItemsModal,
} from './DeleteAllLineItemsModal';

const _getColSpan = (data: CatalogLineItemBackend): number => {
    let colSpan = 7;
    if (data.flags.mep090_tax_types_flag) colSpan += 1;
    else colSpan += 2;
    if (data.data.enableJpWithholdingTax) colSpan += 1;
    return colSpan;
};

const Row: FC<CatalogLineItem> = ({
    cost,
    description,
    hasJpWithholdingTax,
    kind,
    mid,
    price,
    productCode,
    qty,
    tax,
    taxCombination,
    taxRate,
    units,
}) => {
    const {
        data: {enableJpWithholdingTax, features, rights},
        flags,
    } = useBackend<CatalogLineItemBackend>();
    const {mutateAsync} = useDeleteLineItem();

    const handleDelete = async () => {
        await mutateAsync({mid}).then(() => setUrl(window.Urls.catalog_view()));
    };

    return (
        <TableStatic.Row>
            <TableStatic.Cell className="max-w-32">
                <TruncateRight text={productCode} />
            </TableStatic.Cell>
            <TableStatic.Cell
                className="max-w-32"
                colSpan={kind === 'normal' ? 1 : 4}
            >
                <TruncateRight text={description} />
            </TableStatic.Cell>
            {kind === 'normal' && (
                <>
                    <TableStatic.Cell className="text-right">
                        {cost}
                    </TableStatic.Cell>
                    <TableStatic.Cell className="text-right">
                        {qty}
                    </TableStatic.Cell>
                    <TableStatic.Cell className="text-right">
                        {units}
                    </TableStatic.Cell>
                </>
            )}
            {flags.mep090_tax_types_flag && (
                <TableStatic.Cell className="text-left">
                    {taxCombination}
                </TableStatic.Cell>
            )}
            {!flags.mep090_tax_types_flag && (
                <>
                    <TableStatic.Cell className="text-right">
                        {taxRate}
                    </TableStatic.Cell>
                    <TableStatic.Cell>
                        {tax === 'inclusive' && (
                            <Icon
                                className="flex flex-col items-center"
                                icon="favorite_solid"
                                size="base"
                            />
                        )}
                        {tax === 'exclusive' && (
                            <Icon
                                className="flex flex-col items-center"
                                icon="favorite"
                                size="base"
                            />
                        )}
                        {tax === 'not-taxable' && (
                            <Icon
                                className="flex flex-col items-center"
                                icon="danger"
                                size="base"
                            />
                        )}
                    </TableStatic.Cell>
                </>
            )}
            {enableJpWithholdingTax && (
                <TableStatic.Cell>
                    {
                        <Icon
                            className="flex justify-center"
                            icon={hasJpWithholdingTax ? 'checkmark' : 'danger'}
                            size="base"
                        />
                    }
                </TableStatic.Cell>
            )}
            <TableStatic.Cell className="text-right">{price}</TableStatic.Cell>
            <TableStatic.Cell>
                <div className="flex flex-row justify-end gap-2">
                    <TooltipWrapper
                        message={rights.catalogItemCanEdit.failureReason}
                    >
                        <LinkButton
                            disabled={!rights.catalogItemCanEdit.allowed}
                            href={window.Urls.catalog_item_edit_view(mid)}
                            kind="secondary"
                            size="xs"
                        >{t`Edit`}</LinkButton>
                    </TooltipWrapper>
                    <TooltipWrapper
                        message={rights.catalogItemCanDelete.failureReason}
                    >
                        <Button
                            disabled={
                                !rights.catalogItemCanDelete.allowed ||
                                !features.lineItemCatalog
                            }
                            kind="destructive"
                            onClick={handleDelete}
                            size="xs"
                        >{t`Delete`}</Button>
                    </TooltipWrapper>
                </div>
            </TableStatic.Cell>
        </TableStatic.Row>
    );
};
const List = () => {
    const deleteAllModalProps = useDeleteAllLineItemsModal();
    const exportModalProps = useExportHistoryDataModal('cataloglineitem-tax', [
        'search',
    ]);

    const backend = useBackend<CatalogLineItemBackend>();
    const {
        data: {enableJpWithholdingTax, items, pagination, rights},
        flags,
    } = backend;

    const makeLeapsImporter = (
        <a href="/" key="makeleaps-importer">{t`MakeLeaps Importer.`}</a>
    );

    return (
        <>
            <TableStatic className="scroll-shadow-x overflow-x-auto">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column className="w-32">{t`Product Code`}</TableStatic.Column>
                        <TableStatic.Column>{t`Description`}</TableStatic.Column>
                        <TableStatic.Column className="text-right">{t`Cost`}</TableStatic.Column>
                        <TableStatic.Column className="text-right">{t`Qty`}</TableStatic.Column>
                        <TableStatic.Column className="text-right">{t`Units`}</TableStatic.Column>
                        {flags.mep090_tax_types_flag && (
                            <TableStatic.Column className="text-left">{t`Tax`}</TableStatic.Column>
                        )}
                        {!flags.mep090_tax_types_flag && (
                            <>
                                <TableStatic.Column className="text-right">{t`Tax Rate`}</TableStatic.Column>
                                <TableStatic.Column className="text-center">{t`Tax`}</TableStatic.Column>
                            </>
                        )}
                        {enableJpWithholdingTax && (
                            <TableStatic.Column className="w-16 text-center">{t`Wthl.`}</TableStatic.Column>
                        )}
                        <TableStatic.Column className="text-right">{t`Price`}</TableStatic.Column>
                        <TableStatic.Column className="w-36">
                            {
                                <Button
                                    className="float-right font-normal"
                                    kind="secondary"
                                    onClick={() =>
                                        exportModalProps.setIsModalOpen(true)
                                    }
                                    size="xs"
                                >{t`Export`}</Button>
                            }
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {items.length === 0 && (
                        <TableStatic.Row>
                            <TableStatic.Cell
                                colSpan={_getColSpan(backend)}
                            >{jt`Looks like you haven't created any catalog line items yet. You can also import line items using the ${makeLeapsImporter}`}</TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {items.length > 0 &&
                        items.map((value) => (
                            <Row key={value.mid} {...value} />
                        ))}
                </TableStatic.Body>
            </TableStatic>
            <div className="mb-5 flex items-center justify-between">
                <TablePagination
                    isAmbigousStop={true}
                    onChange={(page) => updateUrlParamsAndReload('page', page)}
                    onFirst={() => updateUrlParamsAndReload('page', 1)}
                    onLast={() => updateUrlParamsAndReload('page', 'last')}
                    pageCurrent={pagination.currentPage}
                    pageStop={pagination.numPages}
                    paginationPadding={pagination.windowPages}
                />
                <div className="flex gap-2">
                    {flags.delete_all_catalog_items && (
                        <TooltipWrapper
                            message={rights.catalogItemCanDelete.failureReason}
                        >
                            <Button
                                disabled={!rights.catalogItemCanDelete.allowed}
                                kind="destructive"
                                onClick={() =>
                                    deleteAllModalProps.setIsModalOpen(true)
                                }
                            >{t`Delete All Catalog Items`}</Button>
                        </TooltipWrapper>
                    )}
                    <TooltipWrapper
                        message={rights.catalogItemCanCreate.failureReason}
                    >
                        <LinkButton
                            disabled={!rights.catalogItemCanCreate.allowed}
                            href={window.Urls.catalog_item_create_view()}
                            kind="primary"
                        >{t`Create Catalog Item`}</LinkButton>
                    </TooltipWrapper>
                </div>
            </div>
            <DeleteAllCatalogItems {...deleteAllModalProps} />
            <ExportHistoryDataModal {...exportModalProps} />
        </>
    );
};

export default List;
